import { useMemo, useState } from 'react';

import {
  CATALOGUE_LIMIT,
  groupByCategory,
  matchesSearch,
} from 'src/features/services/serviceUtils';
import { useServiceCategories, useServices } from 'src/features/services/useServices';

/**
 * Which services are picked, out of the catalogue grouped by category.
 *
 * Shared by booking, which starts from nothing, and by changing the services
 * of an appointment, which starts from what it holds. `initialIds` are those:
 * they open picked, and they stay on offer even if retired since. Asking for
 * the active ones only would drop such a service from the list, and saving
 * would then take it off the appointment without anybody choosing to.
 */
export default function useServiceSelection(initialIds: number[] = []) {
  // Read once: the caller builds the array per render, and what the
  // appointment held when the dialog opened is the only thing meant here.
  const [held] = useState(initialIds);
  const [serviceIds, setServiceIds] = useState<number[]>(initialIds);
  const [serviceSearch, setServiceSearch] = useState('');

  const services = useServices({
    ...(held.length === 0 && { isActive: true }),
    limit: CATALOGUE_LIMIT,
    offset: 0,
  });
  const categories = useServiceCategories({ limit: CATALOGUE_LIMIT, offset: 0 });

  const catalogue = useMemo(
    () =>
      (services.data?.items ?? []).filter(
        (service) => service.isActive || held.includes(service.id)
      ),
    [services.data, held]
  );
  const chosen = useMemo(
    () => catalogue.filter((service) => serviceIds.includes(service.id)),
    [catalogue, serviceIds]
  );
  const serviceGroups = useMemo(
    () =>
      groupByCategory(
        catalogue.filter((service) => matchesSearch(service, serviceSearch)),
        categories.data?.items ?? []
      ).filter((group) => group.services.length > 0),
    [catalogue, categories.data, serviceSearch]
  );

  const toggleService = (serviceId: number) =>
    setServiceIds((current) =>
      current.includes(serviceId)
        ? current.filter((id) => id !== serviceId)
        : [...current, serviceId]
    );

  return {
    services,
    catalogue,
    serviceGroups,
    serviceSearch,
    setServiceSearch,
    serviceIds,
    chosen,
    toggleService,
  };
}

export type ServiceSelection = ReturnType<typeof useServiceSelection>;
